<script setup lang="ts">
import { trustStats } from '~/data/trust'

interface Props {
  variant?: 'hero' | 'inline'
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'hero',
})

const { trackEvent } = useAnalytics()

// Hero variant uses full-opacity white + per-line text-shadow because the
// hero overlay was loosened (slate-900/55 → /45) to keep the LCP poster
// from being flagged as "too obscured" by Lighthouse 13. With the lighter
// overlay, text-white/85 dropped under WCAG AA on bright bottom-half
// photos — push contrast onto the text instead.
const containerClass = computed(() =>
  props.variant === 'hero'
    ? 'text-xs uppercase tracking-[0.3em] text-white text-center mb-6 font-body trust-eyebrow-hero'
    : 'text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400 font-body'
)

const dotClass = computed(() =>
  props.variant === 'hero' ? 'mx-2 text-white/70' : 'mx-2 text-slate-400/60'
)
</script>

<template>
  <p :class="containerClass">
    <span class="text-amber-400" aria-hidden="true">★</span>
    <span class="tabular-nums font-medium ms-1">{{ trustStats.rating.toFixed(1) }}</span>
    <span :class="dotClass" aria-hidden="true">·</span>
    <span class="tabular-nums">{{ trustStats.reviewsCount }}</span>
    <span class="ms-1">{{ $t('trust.eyebrow.reviews_label') }}</span>
    <span :class="dotClass" aria-hidden="true">·</span>
    <a
      :href="trustStats.airbnbProfileUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="underline-offset-4 hover:text-amber-300 hover:underline transition-colors"
      @click="trackEvent('airbnb_click', { placement: 'trust_eyebrow', variant: props.variant })"
    >
      {{ $t('trust.eyebrow.superhost') }}
    </a>
  </p>
</template>

<style>
.trust-eyebrow-hero {
  text-shadow:
    0 1px 2px rgba(0, 0, 0, 0.7),
    0 1px 6px rgba(0, 0, 0, 0.45);
}
</style>
